/**
 * Waitlist signup — wraps the `joinWaitlistCallable` Cloud Function.
 *
 * Backend source of truth:
 * `home-ceo-backend/functions/typescript/src/callables/join-waitlist.ts`.
 * The function writes the signup to a Firestore collection (admin-side)
 * after passing App Check enforcement and the `requireWebClient` provider-
 * source check. Idempotent — re-submitting the same email returns
 * `alreadyOnWaitlist: true`.
 *
 * Request and response types come from
 * `@latlonglabs/home-ceo-backend-contracts` so backend shape changes
 * surface as a typecheck failure here, not a silent runtime mismatch.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import type {
  JoinWaitlistRequest,
  JoinWaitlistResponse,
} from '@latlonglabs/home-ceo-backend-contracts';
import { app } from '@config/firebase-config';

const functions = getFunctions(app, 'us-west1');

interface WaitlistResult {
  /** True when the email was already on the waitlist before this call. */
  alreadyOnWaitlist: boolean;
}

export async function submitWaitlist(
  input: JoinWaitlistRequest,
): Promise<WaitlistResult> {
  const fn = httpsCallable<JoinWaitlistRequest, JoinWaitlistResponse>(
    functions,
    'joinWaitlistCallable',
  );
  const result = await fn(input);
  if (!result.data.success) {
    // Defensive — backend always returns success:true on the success path;
    // failures come back as HttpsError. This branch is unreachable today.
    throw new Error('Waitlist signup did not succeed');
  }
  return { alreadyOnWaitlist: result.data.alreadyOnWaitlist };
}
